export const NA = 'DATA NOT AVAILABLE';

export const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

export const fmt = (v, digits = 2) =>
  isNum(v) ? v.toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits }) : null;

export const dateLabel = (iso, withYear = true) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(iso ?? ''))) return iso ?? '—';
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  });
};

export const crore = (v) => {
  if (!isNum(v)) return null;
  return v >= 100000 ? `₹${fmt(v / 100000, 2)} L Cr` : `₹${fmt(v, 0)} Cr`;
};

/**
 * Splits a rupee amount into the parts <Money> styles separately.
 * Precision scales with size: two decimals on a ₹17,801 share price is noise
 * that lengthens the number without telling the reader anything, while a ₹4.35
 * penny stock needs them.
 */
export function moneyParts(v, digits) {
  if (!isNum(v)) return null;
  const abs = Math.abs(v);
  // Zero is not a sub-rupee value: it belongs with ordinary amounts, or
  // "no dividend" renders as ₹0.0000.
  const places = digits ?? (abs === 0 ? 2 : abs >= 1000 ? 0 : abs >= 1 ? 2 : 4);
  const [int, dec] = abs
    .toLocaleString('en-IN', { minimumFractionDigits: places, maximumFractionDigits: places })
    .split('.');
  return { sign: v < 0 ? '-' : '', int, dec: dec ?? null };
}

/** Compact rupee scale for axis labels, chips and other tight spaces. */
export const moneyShort = (v) => {
  if (!isNum(v)) return null;
  const a = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (a >= 1e7) return `${sign}₹${fmt(a / 1e7, a >= 1e8 ? 0 : 1)} Cr`;
  if (a >= 1e5) return `${sign}₹${fmt(a / 1e5, 1)} L`;
  if (a >= 1e3) return `${sign}₹${fmt(a / 1e3, 1)}K`;
  return `${sign}₹${fmt(a, 0)}`;
};

export const signClass = (v) => (!isNum(v) ? 'neu' : v > 0 ? 'pos' : v < 0 ? 'neg' : 'neu');

/** Direction word to an Icon name. Callers render it; this stays presentation-free. */
export const arrow = (dir) =>
  ({
    rising: 'trendUp', falling: 'trendDown', flat: 'trendFlat',
    growing: 'trendUp', declining: 'trendDown', expanding: 'trendUp', shrinking: 'trendDown'
  })[dir] ?? null;

/** Maps a verdict word onto one of the badge colour classes. */
export function badgeClass(word) {
  const w = String(word ?? '').toUpperCase();
  if (/CHEAP|LOW RISK|STRONG|COMFORTABLE|HEALTHY|UNDERVALUED|ACCELERATING|LEADING|ADDING|BUYING|CLEAR|COMPOUNDER|CONSISTENT|SUSTAINABLE|PROFITABLE GROWTH|SMART MONEY/.test(w)) return 'ok';
  if (/EXPENSIVE|HIGH DEBT|DANGER|RISK|RED FLAG|OVERVALUED|DECLINING|LAGGING|REDUCING|SELLING|CAPITAL DESTROYER|EXITING|ABOVE EARNINGS|TOP-LINE ONLY/.test(w)) return 'bad';
  if (/FAIR|MODERATE|WATCH|TIGHT|STABLE|STEADY|SLOWING|MIDDLE OF PACK|NEUTRAL|AVERAGE|MIXED|STRETCHED|IRREGULAR|HOLDING STEADY/.test(w)) return 'warn';
  return 'neu';
}

export const toneClass = (tone) => (tone === 'green' ? 'green' : tone === 'red' ? 'red' : 'amber');

export const trim = (s, n) => (!s ? '' : s.length > n ? `${s.slice(0, n)}…` : s);

export const describeFilters = (f) => {
  const bits = [];
  if (f.minMarketCapCr != null) bits.push(`market cap ≥ ₹${f.minMarketCapCr.toLocaleString('en-IN')} Cr`);
  if (f.maxMarketCapCr != null) bits.push(`market cap ≤ ₹${f.maxMarketCapCr.toLocaleString('en-IN')} Cr`);
  if (f.maxSharePrice != null) bits.push(`share price ≤ ₹${f.maxSharePrice.toLocaleString('en-IN')}`);
  if (f.minRoe != null) bits.push(`ROE ≥ ${f.minRoe}%`);
  if (f.maxPe != null) bits.push(`P/E ≤ ${f.maxPe}`);
  if (f.maxDebtEquity != null) bits.push(`D/E ≤ ${f.maxDebtEquity} (non-financials)`);
  if (f.maxPledgePct != null) bits.push(`promoter pledge ≤ ${f.maxPledgePct}%`);
  if (f.minDailyTurnoverCr != null) bits.push(`daily turnover ≥ ₹${f.minDailyTurnoverCr} Cr`);
  if (f.maxInstitutionalPct != null) bits.push(`institutional holding ≤ ${f.maxInstitutionalPct}%`);
  if (f.requirePositiveFcf) bits.push('positive free cash flow required');
  if (f.excludeOvervalued) bits.push('overvalued names excluded');
  return bits.join(' · ') || 'none';
};
